import { registerOfflineManifest } from '@floorfile/offline'
import type { OfflineManifest } from '@floorfile/contracts'
import { ncrOfflineManifest } from '@/features/ncr'
import { changeControlOfflineManifest } from '@/features/change-control'
import { supplierQualityOfflineManifest } from '@/features/supplier-quality'
import { riskFmeaOfflineManifest } from '@/features/risk-fmea'
import { deviationsOosOfflineManifest } from '@/features/deviations-oos'
import { lotsOfflineManifest } from '@/features/lots'
import { esignOfflineManifest } from '@/features/esign'
import { hrRecordsOfflineManifest } from '@/features/hr-records'

/**
 * The D24 coverage contract (OFFLINE_&_MOBILE_RESILIENCE.md §11): every
 * floor-facing module's owner declaration, registered into the shared
 * offline runtime. The core-execution manifests live here beside the
 * registration; the Advanced-QMS/HR modules published theirs from their own
 * slices (including HR's binding never-cache declaration).
 */

const checksOfflineManifest: OfflineManifest = {
  module: 'checks',
  entities: ['check'],
  actions: ['list-my-checks', 'start', 'fill', 'photo', 'sign', 'complete', 'comment'],
  conflictPolicy: 'terminal-idempotent',
  requiresOnline: ['raise-car', 'create-plan', 'summary'],
}

const equipmentOfflineManifest: OfflineManifest = {
  module: 'equipment',
  entities: ['equipment', 'downtime_event'],
  actions: ['report-downtime', 'resolve-downtime'],
  conflictPolicy: 'terminal-idempotent',
  requiresOnline: ['create-equipment', 'set-status', 'metrics'],
}

const maintenanceOfflineManifest: OfflineManifest = {
  module: 'maintenance',
  entities: ['work_order'],
  actions: ['toggle-step', 'complete-work-order'],
  conflictPolicy: 'terminal-idempotent',
  requiresOnline: ['create-plan', 'generate', 'reschedule', 'calendar'],
}

const partsOfflineManifest: OfflineManifest = {
  module: 'parts',
  entities: ['part'],
  actions: ['record-consumption', 'request-reorder'],
  conflictPolicy: 'terminal-idempotent',
  requiresOnline: ['create-part', 'csv-import', 'requisition-approval'],
}

const incidentsOfflineManifest: OfflineManifest = {
  module: 'incidents',
  entities: ['incident', 'help_request'],
  // Help/andon is DEGRADED (doc §2): it queues, but cannot broadcast until
  // online — the UI warns and suggests the physical fallback.
  actions: ['report-incident', 'photo', 'request-help'],
  conflictPolicy: 'terminal-idempotent',
  requiresOnline: ['investigate', 'resolve', 'stats'],
}

/**
 * FF-QA-142/143: a CAR raised from the floor (a failed check, an incident
 * walk-round) must not be lost in a dead zone. Creation queues; every later
 * lifecycle step (analysis, submit, verify, close) is an online review action.
 */
const carsOfflineManifest: OfflineManifest = {
  module: 'cars',
  entities: ['car'],
  actions: ['create-car'],
  conflictPolicy: 'terminal-idempotent',
  requiresOnline: ['analysis', 'actions', 'submit', 'verify', 'close', 'reopen', 'assign'],
}

/**
 * FF-QA-143: an audit is walked on the floor like a check. Answering,
 * findings and completion (with its signature) queue; scheduling, ownership
 * and the lifecycle around the walk are online review actions.
 */
const auditsOfflineManifest: OfflineManifest = {
  module: 'audits',
  entities: ['audit', 'audit_finding'],
  actions: ['save-responses', 'add-finding', 'remove-finding', 'sign', 'complete'],
  conflictPolicy: 'terminal-idempotent',
  requiresOnline: ['schedule', 'start', 'take-ownership', 'edit', 'cancel', 'escalate-finding'],
}

const commentsOfflineManifest: OfflineManifest = {
  module: 'comments',
  entities: ['comment'],
  actions: ['create-comment'],
  conflictPolicy: 'terminal-idempotent',
  requiresOnline: ['edit', 'react', 'resolve-thread'],
}

let registered = false

export function registerAllOfflineManifests(): void {
  if (registered) return
  registered = true
  for (const manifest of [
    checksOfflineManifest,
    equipmentOfflineManifest,
    maintenanceOfflineManifest,
    partsOfflineManifest,
    incidentsOfflineManifest,
    carsOfflineManifest,
    commentsOfflineManifest,
    auditsOfflineManifest,
    ncrOfflineManifest,
    changeControlOfflineManifest,
    supplierQualityOfflineManifest,
    riskFmeaOfflineManifest,
    deviationsOosOfflineManifest,
    lotsOfflineManifest,
    esignOfflineManifest,
    hrRecordsOfflineManifest,
  ]) {
    registerOfflineManifest(manifest)
  }
}
